import { useState } from 'react';
import { FiLock, FiEye, FiEyeOff } from 'react-icons/fi';

/* Password input cu eye toggle — wrapper minimal peste TextField intent
 * pentru ca paginile auth să nu repete state-ul de show/hide la fiecare
 * câmp parolă. Forward-uim props-urile către <input> (autoComplete,
 * minLength, value, onChange, etc.).
 *
 * Două variante (mirror la TextField):
 *   - default (label-above + lock icon prefix): pentru Register / Pro pages.
 *   - floating (Airbnb-style): eticheta animată în interiorul input-ului,
 *     fără lock icon, eye toggle păstrat în dreapta. Activat cu `floating`.
 */
export default function PasswordField({
  id,
  label = 'Parolă',
  required = false,
  floating = false,
  className = '',
  ...inputProps
}) {
  const [show, setShow] = useState(false);
  // Mirror the input's `disabled` state onto the eye toggle so a
  // disabled password field doesn't have a still-clickable show button.
  // Note: `readOnly` is intentionally NOT propagated — a readOnly
  // field typically still allows viewing the stored value (the user
  // just can't edit it), so the toggle stays useful.
  const isDisabled = !!inputProps.disabled;

  if (floating) {
    return (
      <div className={`relative ${className}`}>
        <input
          {...inputProps}
          id={id}
          type={show ? 'text' : 'password'}
          required={required}
          placeholder={label || ' '}
          className="peer w-full pl-6 pr-12 pt-5 pb-1.5 border border-neutral-200 rounded-full text-sm text-neutral-950 placeholder-transparent focus:outline-none focus:border-neutral-950 focus:ring-0 transition disabled:bg-neutral-50 disabled:text-neutral-600 disabled:cursor-not-allowed"
        />
        <label
          htmlFor={id}
          className="absolute left-6 top-1.5 text-[10px] font-bold text-neutral-500 uppercase tracking-wider transition-all
                     peer-placeholder-shown:text-sm peer-placeholder-shown:top-3.5 peer-placeholder-shown:font-normal peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal
                     peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:font-bold peer-focus:uppercase peer-focus:tracking-wider peer-focus:text-neutral-600
                     peer-disabled:text-neutral-600"
        >
          {label}
        </label>
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          disabled={isDisabled}
          aria-label={show ? 'Ascunde parola' : 'Afișează parola'}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 text-neutral-400 enabled:hover:text-neutral-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {show ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
        </button>
      </div>
    );
  }

  return (
    <div className={className}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}{required && <span className="text-orange-500"> *</span>}
      </label>
      <div className="relative">
        <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          // Spread first ca tipul controlat de show-toggle să nu poată fi
          // suprascris de un prop type accidental venit de la caller —
          // PasswordField e specializat, type e contractul intern.
          {...inputProps}
          id={id}
          type={show ? 'text' : 'password'}
          required={required}
          className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition bg-white text-sm placeholder:text-gray-400 disabled:bg-gray-50 disabled:text-gray-500"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          disabled={isDisabled}
          aria-label={show ? 'Ascunde parola' : 'Afișează parola'}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 enabled:hover:text-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {show ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
